/* eslint-disable */
import type { CountryCodes, firebase } from '../firebase/firestore';

export type WeekDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type Month = number;
export type Season = 'autumn' | 'spring' | 'summer' | 'winter';
export type IWedding_Website_Section =
  | IWedding_Website_Section_VenueField
  | IWedding_Website_Section_PhotosField
  | IWedding_Website_Section_QuestionAnswerField
  | IWedding_Website_Section_TextField
  | IWedding_Website_Section_GiftListField
  | IWedding_Website_Section_HotelsField
  | IWedding_Website_Section_ScheduleField;
export type IWedding_Website_Sections = IWedding_Website_Section[];
export type IWeddingID = string;

export interface IWedding {
  bookings?: {
    beauty?: boolean;
    cakes?: boolean;
    catering?: boolean;
    decoration?: boolean;
    dress?: boolean;
    entertainment?: boolean;
    florist?: boolean;
    jewellery?: boolean;
    marquee?: boolean;
    menswear?: boolean;
    music?: boolean;
    photo?: boolean;
    planners?: boolean;
    stationery?: boolean;
    transport?: boolean;
    venue?: boolean;
    video?: boolean;
  };
  bookmarks?: number[];
  budget?: null | number;
  costs?: IWedding_Cost;
  createdAt: firebase.firestore.Timestamp;
  /**
   * Supplier ID
   */
  createdBy?: string;
  date?: IDatePicker;
  dateEngagement?: IDatePicker;
  draft?: IWedding_Draft;
  guests: IWedding_Guest;
  hasFullyOnboarded?: boolean;
  id: IWeddingID;
  l10n: IWedding_L10N;
  location: null | IAddress;
  microsite?: string;
  /**
   * AI-generated website themes, one entry per theme. Server-written only.
   */
  motifThemes?: IWedding_MotifTheme[];
  notes: IWedding_Note;
  /**
   * @minItems 1
   */
  partners: [string, ...string[]];
  photoCover?: {
    path: string;
    portrait?: boolean;
  };
  planningStage: IWedding_PlanningStage;
  roles?: string[];
  suppliers: IWedding_Supplier;
  tasks: IWedding_Task;
  updatedAt?: firebase.firestore.Timestamp;
  users: string[];
  website?: IWedding_Website;
  rsvp?: IWedding_RSVP;
}
export interface IWedding_Cost {
  actual: number;
  estimate: number;
}
/**
 * This interface was referenced by `IWedding`'s JSON-Schema
 * via the `definition` "datePicker".
 */
export interface IDatePicker {
  d: number | WeekDay | null | WeekDay[];
  m: Month | Season | null | Month[];
  y: number | null;
}
export interface IWedding_Draft {
  website?: IWedding_Website;
  date?: IDatePicker;
  partners?: string[];
  meta: IWedding_DraftMeta;
}
/**
 * This interface was referenced by `IWedding`'s JSON-Schema
 * via the `definition` "website".
 */
export interface IWedding_Website {
  config: {
    theme: {
      font: string;
      palette: string;
    };
    rsvpVisible?: boolean;
    layout?: Layout;
    /**
     * The selected Motif theme. Absent for other layouts.
     */
    motif?: {
      /**
       * Theme CSS pointer; the R2 filename guests read.
       */
      cssHash?: string;
      /**
       * Which theme entry the selection points at.
       */
      themeId?: string;
    };
  };
  id?: string;
  privacy: {
    pin: string;
    published: boolean;
  };
  sections: IWedding_Website_Sections;
}
export interface IWedding_Website_Section_VenueField {
  data: {
    supplier: string;
    useVenuePhotos: boolean;
  };
  name: string;
  type: 'VenueField';
  visible: boolean;
}
export interface IWedding_Website_Section_PhotosField {
  data: {
    height?: number;
    path: string;
    width?: number;
  }[];
  name: string;
  type: 'PhotosField';
  visible: boolean;
}
export interface IWedding_Website_Section_QuestionAnswerField {
  data: {
    answer: string;
    question: string;
  }[];
  name: string;
  type: 'QuestionAnswerField';
  visible: boolean;
}
export interface IWedding_Website_Section_TextField {
  data: {
    value: string;
  };
  name: string;
  type: 'TextField';
  visible: boolean;
}
export interface IWedding_Website_Section_GiftListField {
  data: {
    message: string;
    url: string;
  };
  name: string;
  type: 'GiftListField';
  visible: boolean;
}
export interface IWedding_Website_Section_HotelsField {
  data: {
    message?: string;
  };
  name: string;
  type: 'HotelsField';
  visible: boolean;
}
export interface IWedding_Website_Section_ScheduleField {
  name: string;
  type: 'ScheduleField';
  visible: boolean;
}
export interface IWedding_DraftMeta {
  updatedAt: firebase.firestore.Timestamp;
}
export interface IWedding_Guest {
  categories: IWedding_GuestCategories;
  count: number;
  estimate?: null | number;
  events?: IWedding_Event;
  tables?: IWedding_GuestTables;
}
export interface IWedding_GuestCategories {
  data: {
    [k: string]: string;
  };
  sort: string[];
}
/**
 * @deprecated Use events subcollection instead.
 */
export interface IWedding_Event {
  wedding: string[];
}
export interface IWedding_GuestTables {
  data: {
    [k: string]: string;
  };
  sort: string[];
}
export interface IWedding_L10N {
  /**
   * ISO 3166-1 Alpha-2 Code
   */
  country: CountryCodes;
  /**
   * ISO 4217 Code
   */
  currency: string;
}
/**
 * This interface was referenced by `IWedding`'s JSON-Schema
 * via the `definition` "address".
 */
export interface IAddress {
  adminArea?: string[];
  city?: string;
  country?: CountryCodes;
  name?: string;
  postalCode?: string;
  provider?: {
    id: string;
    type: 'google.places';
  };
  street?: string[];
}
export interface IWedding_MotifTheme {
  id: string;
  status: IWedding_MotifTheme_Status;
  /**
   * Fencing token for the generation run that owns this entry.
   */
  generationId: string;
  /**
   * When the current generation started.
   */
  startedAt: firebase.firestore.Timestamp;
  /**
   * Failure reason when status is 'failed'.
   */
  error?: string;
  /**
   * Ready versions, oldest first; the last entry is the current draft.
   */
  history: IWedding_MotifTheme_HistoryEntry[];
}
export interface IWedding_MotifTheme_HistoryEntry {
  cssHash: string;
  createdAt: firebase.firestore.Timestamp;
}
export interface IWedding_Note {
  count: number;
}
export interface IWedding_PlanningStage {
  hasBudget: boolean;
  hasGuests: boolean;
  hasNotStarted: boolean;
  hasVenue: boolean;
  hasVenueStyle: boolean;
  hasWeddingDate: boolean;
}
export interface IWedding_Supplier {
  count: number;
}
export interface IWedding_Task {
  done: number;
  initializedAt?: firebase.firestore.Timestamp;
  total: number;
}
/**
 * This interface was referenced by `IWedding`'s JSON-Schema
 * via the `definition` "rsvp".
 */
export interface IWedding_RSVP {
  deadline?: firebase.firestore.Timestamp;
  questions: ({
    id: string;
    question: string;
    events: string[];
  } & (
    | {
        type: 'boolean';
      }
    | {
        type: 'list';
        options: string[];
      }
    | {
        type: 'string';
      }
  ))[];
}
/**
 * This interface was referenced by `IWedding`'s JSON-Schema
 * via the `definition` "guestBucket".
 */
export interface GuestBucket {
  data: {
    [k: string]: string;
  };
  sort: string[];
}

/**
 * Which layout to use. legacy or undefined is the old one, primary is the new one.
 * Split is the new split-view layout with photo carousel on left and content on right.
 * Motif is the AI-themed layout.
 */
export enum Layout {
  Legacy = 'legacy',
  Primary = 'primary',
  Split = 'split',
  Motif = 'motif',
}
export enum IWedding_MotifTheme_Status {
  Generating = 'generating',
  Ready = 'ready',
  Failed = 'failed',
}
